import { useCallback, useEffect } from 'react'
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form'
import { clearLocalStorageValue, useLocalStorage } from './useLocalStorage'

interface PropsIn<T> {
  cacheKey: string
  getValues: UseFormGetValues<T>
  setValue: UseFormSetValue<T>
  enabled?: boolean
}

interface PropsOut {
  resetCache: () => void
}

export const useCacheFormValues = <T>({
  cacheKey,
  getValues,
  setValue,
  enabled = true
}: PropsIn<T>): PropsOut => {
  const [cachedValue, setCachedValue] = useLocalStorage<T>(cacheKey, getValues())

  const onReMount = useCallback(() => {
    if (cachedValue) {
      Object.entries(cachedValue).forEach(([key, value]) => {
        setValue(key as any, value as any)
      })
    }
  }, [cachedValue])

  const onUnMount = useCallback(() => {
    setCachedValue(getValues())
  }, [])

  useEffect(() => {
    if (enabled === true) onReMount()
    return () => {
      if (enabled === true) onUnMount()
    }
  }, [cacheKey])

  const resetCache = useCallback(() => {
    // Clear storage immediately so a same-turn unmount cannot revive the draft
    // if React drops the setState(null) updater.
    clearLocalStorageValue(cacheKey)
    setCachedValue(null)
  }, [cacheKey])

  return {
    resetCache
  }
}

export const useResetCacheFormValues = (cacheKey: string) => {
  const setCachedValue = useLocalStorage(cacheKey, null)[1]

  const resetCache = useCallback(() => {
    clearLocalStorageValue(cacheKey)
    setCachedValue(null)
  }, [cacheKey])

  return {
    resetCache
  }
}
